import React, { useState } from "react";
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { isStatusUnmarkedByDefault } from "../hooks/useDashboardFilters";

export interface DashboardFilters {
  texto: string;
  severidade: string[];
  categoriaGrupo: string;
  status: string[];
  slaStatus: string[];
  mesApuracao: string;
  /** Vazio = todos. Fica salvo no perfil da pessoa (06/10/2026). */
  produtos: string[];
}

interface DashboardFiltersProps {
  filters: DashboardFilters;
  onFiltersChange: (filters: DashboardFilters) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  activeFiltersCount: number;
  severidadeOptions: Array<{ value: string; label: string }>;
  statusOptions: Array<{ value: string; label: string }>;
  mesApuracaoOptions: Array<{ value: string; label: string }>;
  produtoOptions: string[];
}

export const DashboardFiltersComponent: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  hasActiveFilters,
  severidadeOptions,
  statusOptions,
  mesApuracaoOptions,
  produtoOptions,
}) => {
  const handleInputChange = (field: keyof DashboardFilters, value: string) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  const handleSeveridadeToggle = (value: string) => {
    const updated = filters.severidade.includes(value)
      ? filters.severidade.filter((v) => v !== value)
      : [...filters.severidade, value];
    onFiltersChange({ ...filters, severidade: updated });
  };

  const handleStatusToggle = (value: string) => {
    const updated = filters.status.includes(value)
      ? filters.status.filter((v) => v !== value)
      : [...filters.status, value];
    onFiltersChange({ ...filters, status: updated });
  };

  const handleProdutoToggle = (value: string) => {
    const updated = filters.produtos.includes(value)
      ? filters.produtos.filter((v) => v !== value)
      : [...filters.produtos, value];
    onFiltersChange({ ...filters, produtos: updated });
  };

  const handleSlaStatusToggle = (value: string) => {
    const updated = filters.slaStatus.includes(value)
      ? filters.slaStatus.filter((v) => v !== value)
      : [...filters.slaStatus, value];
    onFiltersChange({ ...filters, slaStatus: updated });
  };

  const slaStatusOptions = [
    { value: "pausado", label: "Pausado" },
    { value: "aguardando_fila", label: "Aguardando na Fila" },
    { value: "concluido_prazo", label: "No Prazo" },
    { value: "concluido_atraso", label: "Com Atraso" },
  ];

  const allSeveridadeValues = severidadeOptions.map((o) => o.value);
  const defaultStatusValues = statusOptions
    .filter((o) => !isStatusUnmarkedByDefault(o.label))
    .map((o) => o.value);
  const allSlaValues = slaStatusOptions.map((o) => o.value);

  const severidadeRestrictive =
    allSeveridadeValues.length > 0 &&
    filters.severidade.length !== allSeveridadeValues.length;
  const statusIsDefault =
    filters.status.length === defaultStatusValues.length &&
    defaultStatusValues.every((v) => filters.status.includes(v));
  const statusRestrictive = defaultStatusValues.length > 0 && !statusIsDefault;
  const slaRestrictive = filters.slaStatus.length !== allSlaValues.length;

  // As etiquetas de status, severidade e SLA nascem escondidas (05/10/2026): o padrão serve à maior
  // parte das vezes. O botão diz quantos grupos estão fora do padrão, para filtro ligado não passar
  // despercebido com o painel fechado.
  const [mostrarEtiquetas, setMostrarEtiquetas] = useState(false);
  const gruposAlterados = [
    filters.produtos.length > 0,
    statusRestrictive,
    severidadeRestrictive,
    slaRestrictive,
  ].filter(Boolean).length;

  return (
    <div className="space-y-3">
      {/* Linha 1: Tipo (segmented) + Busca + Selects compactos */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Segmented Control — Tipo de Chamado */}
        <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
          <button
            onClick={() => handleInputChange("categoriaGrupo", "suporte")}
            className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-all duration-150 ${
              filters.categoriaGrupo === "suporte"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Suporte
          </button>
          <button
            onClick={() =>
              handleInputChange("categoriaGrupo", "novas_funcionalidades")
            }
            className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-all duration-150 ${
              filters.categoriaGrupo === "novas_funcionalidades"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Novas Funcionalidades
          </button>
        </div>

        {/* Separador */}
        <div className="w-px h-6 bg-gray-200" />

        {/* Mês */}
        <select
          value={filters.mesApuracao}
          onChange={(e) => handleInputChange("mesApuracao", e.target.value)}
          className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all cursor-pointer"
        >
          {mesApuracaoOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Busca */}
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={filters.texto}
            onChange={(e) => handleInputChange("texto", e.target.value)}
            placeholder="Buscar..."
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all placeholder:text-gray-400"
          />
          {filters.texto && (
            <button
              onClick={() => handleInputChange("texto", "")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMostrarEtiquetas((v) => !v)}
          aria-expanded={mostrarEtiquetas}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
            mostrarEtiquetas || gruposAlterados > 0
              ? "border-blue-200 bg-blue-50 text-blue-700"
              : "border-gray-200 bg-white text-gray-600 hover:text-gray-800"
          }`}
        >
          <AdjustmentsHorizontalIcon className="w-4 h-4" />
          Filtros
          {gruposAlterados > 0 && (
            <span className="rounded-full bg-blue-600 px-1.5 text-xs font-semibold text-white">{gruposAlterados}</span>
          )}
        </button>

        {/* Limpar tudo */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Linha 2: Status + Severidade + SLA como pills, só com o botão Filtros aberto */}
      {mostrarEtiquetas && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Produto: nenhum marcado = todos. Fica salvo no perfil (06/10/2026). */}
          {produtoOptions.length > 0 && (
            <>
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">
                Produto
              </span>
              {produtoOptions.map((produto) => {
                const isActive = filters.produtos.includes(produto);
                return (
                  <button
                    key={produto}
                    onClick={() => handleProdutoToggle(produto)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all duration-150 ${
                      isActive
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700"
                    }`}
                  >
                    {produto}
                  </button>
                );
              })}
              {filters.produtos.length > 0 && (
                <button
                  onClick={() => onFiltersChange({ ...filters, produtos: [] })}
                  className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  todos
                </button>
              )}
              <div className="w-px h-4 bg-gray-200 mx-1" />
            </>
          )}

          {/* Status pills */}
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">
            Status
          </span>
          {statusOptions.map((option) => {
            const isActive = filters.status.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => handleStatusToggle(option.value)}
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all duration-150 ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                {option.label}
              </button>
            );
          })}
          {statusRestrictive && (
            <button
              onClick={() =>
                onFiltersChange({ ...filters, status: defaultStatusValues })
              }
              className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              limpar
            </button>
          )}

          {/* Separador */}
          <div className="w-px h-4 bg-gray-200 mx-1" />

          {/* Severidade pills */}
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">
            Severidade
          </span>
          {severidadeOptions.map((option) => {
            const isActive = filters.severidade.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => handleSeveridadeToggle(option.value)}
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all duration-150 ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                {option.label}
              </button>
            );
          })}
          {severidadeRestrictive && (
            <button
              onClick={() =>
                onFiltersChange({ ...filters, severidade: allSeveridadeValues })
              }
              className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              limpar
            </button>
          )}

          {/* Separador */}
          <div className="w-px h-4 bg-gray-200 mx-1" />

          {/* SLA pills */}
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">
            SLA
          </span>
          {slaStatusOptions.map((option) => {
            const isActive = filters.slaStatus.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => handleSlaStatusToggle(option.value)}
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all duration-150 ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                {option.label}
              </button>
            );
          })}
          {slaRestrictive && (
            <button
              onClick={() =>
                onFiltersChange({ ...filters, slaStatus: allSlaValues })
              }
              className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              limpar
            </button>
          )}
        </div>
      )}
    </div>
  );
};
